import { Modal, Icon, Button } from '@distribusion/owl-frontend-ui'
import { Field, useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { useUserAuth } from '@hooks/userAuthProvider/provider'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { password, required, chain, exact } from '@lib/validators'
import { useChangePassword } from '@loaders/changePassword'
import { AccountFormData } from '@pages/Account/Form/Provider'
import { ModalType, MODAL_TYPES } from '@pages/Account/hooks/usePasswordChange'
import { ForgotPasswordModal } from '@pages/Account/Settings/Modals'
import { ForgotPasswordButton } from '@pages/Account/Settings/Modals/ForgotPasswordButton'
import PasswordField from '@ui/Input/PasswordField'

import '@pages/Account/Settings/Modals/ChangePasswordModal/index.scss'

interface ChangePasswordModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onError: (errorType: ModalType) => void
  onForgotPasswordSuccess: (email: string) => void
}

export const ChangePasswordModal = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  onForgotPasswordSuccess,
}: ChangePasswordModalProps) => {
  const { t } = useTranslation()
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const { values, errors, touched, setFieldValue } = useFormikContext<AccountFormData>()
  const { user } = useUserAuth()
  const [searchParams] = useSearchParams()
  const { retailerPartnerNumber } = useParams()

  const { mutateAsync: changePassword, isLoading } = useChangePassword()

  const verificationCode = searchParams.get('verification_code')
  const hasVerificationCode = !!verificationCode
  const currentPassword = values.settings.currentPassword?.trim() || ''
  const newPassword = values.settings.newPassword?.trim() || ''
  const confirmPassword = values.settings.confirmPassword?.trim() || ''
  const hasCurrentPasswordError = touched.settings?.currentPassword && !!errors.settings?.currentPassword
  const hasNewPasswordError = touched.settings?.newPassword && !!errors.settings?.newPassword
  const hasConfirmPasswordError = touched.settings?.confirmPassword && !!errors.settings?.confirmPassword
  const passwordsMatch = newPassword === confirmPassword

  const missingFields = (!hasVerificationCode && !currentPassword) || !newPassword || !confirmPassword
  const hasError = hasCurrentPasswordError || hasNewPasswordError || hasConfirmPasswordError
  const isButtonDisabled = missingFields || hasError || !passwordsMatch

  useEffect(() => {
    if (!isOpen) {
      setFieldValue('settings.currentPassword', '')
      setFieldValue('settings.newPassword', '')
      setFieldValue('settings.confirmPassword', '')
    }
  }, [isOpen, setFieldValue])

  const handleSubmit = async (): Promise<void> => {
    if (!user?.id) return

    try {
      await changePassword({
        userId: user.id,
        retailerPartnerNumber: Number(retailerPartnerNumber),
        newPassword,
        ...(hasVerificationCode ? { verification_code: verificationCode } : { current_password: currentPassword }),
      })

      onSuccess()
    } catch (error) {
      onError(MODAL_TYPES.CHANGE_PASSWORD)
    }
  }

  const handleForgotPasswordClick = () => {
    onClose()
    setShowForgotPassword(true)
  }

  const handleForgotPasswordClose = () => {
    setShowForgotPassword(false)
  }

  const handleForgotPasswordSuccess = (email: string) => {
    setShowForgotPassword(false)
    onForgotPasswordSuccess(email)
  }

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={onClose}
        title={t('account.settings.signIn.passwordModal.title')}
        maxWidth="sm"
        dataTag="change-password-modal"
      >
        <div className={bem('password-modal', 'content')}>
          {!hasVerificationCode && (
            <>
              <Field
                component={PasswordField}
                label={t('account.settings.signIn.passwordModal.currentPassword')}
                name="settings.currentPassword"
                type="password"
                validate={chain([required])}
                required
                dataTag="current-password-input"
              />
              <ForgotPasswordButton onClick={handleForgotPasswordClick} />
            </>
          )}
          <Field
            component={PasswordField}
            label={t('account.settings.signIn.passwordModal.newPassword')}
            name="settings.newPassword"
            type="password"
            validate={chain([required, password])}
            required
            dataTag="new-password-input"
          />
          <div className={bem('password-modal', 'password-info')}>
            <Icon name="info" size="medium" className={bem('password-info', 'icon')} />
            <p className={bem('password-info', 'text')}>{t('checkout.createAccount.hint')}</p>
          </div>
          <Field
            component={PasswordField}
            label={t('account.settings.signIn.passwordModal.confirmPassword')}
            name="settings.confirmPassword"
            type="password"
            validate={chain([
              required,
              value => exact(values.settings.newPassword ?? '', t('errors.invalidPasswordMatch'))(value),
            ])}
            required
            dataTag="confirm-password-input"
          />
          <Button
            color="primary"
            className={bem('password-modal', 'submit-button')}
            onClick={handleSubmit}
            disabled={isButtonDisabled}
            loading={isLoading}
            dataTag="submit-password-change"
          >
            {t('account.settings.signIn.passwordModal.button')}
          </Button>
        </div>
      </Modal>
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={handleForgotPasswordClose}
        onSuccess={handleForgotPasswordSuccess}
        onError={onError}
        initialEmail={user?.email}
      />
    </>
  )
}
